import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Mail, Phone, Trash2, Check, Inbox, RefreshCw } from "lucide-react";

export default function EnquiriesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setItems(Array.isArray(data) ? data : []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id, read) => {
    try {
      await api.post(`/enquiries/${id}/read?read=${read}`);
      setItems((list) => list.map((e) => (e.id === id ? { ...e, read } : e)));
    } catch (_) { /* ignore */ }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/enquiries/${id}`);
      setItems((list) => list.filter((e) => e.id !== id));
    } catch (_) { /* ignore */ }
  };

  const unread = items.filter((e) => !e.read).length;

  return (
    <div className="space-y-5" data-testid="enquiries-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <Inbox size={20} className="text-[var(--dojo-maroon)]" /> Enquiries
          </h2>
          <p className="text-sm text-[var(--dojo-ink-soft)]">
            Website "Begin your Journey" submissions · {unread} unread
          </p>
        </div>
        <button onClick={load} className="btn-outline flex items-center gap-2 text-xs" data-testid="enquiries-refresh">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--dojo-ink-soft)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-[var(--dojo-border)] bg-[var(--dojo-paper-alt)] p-10 text-center text-[var(--dojo-ink-soft)]">
          No enquiries yet. Submissions from the home page form will appear here.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <div
              key={e.id}
              className={`border p-4 flex flex-col md:flex-row md:items-center gap-3 ${
                e.read ? "border-[var(--dojo-border)] bg-[var(--dojo-paper)]" : "border-[var(--dojo-maroon)] bg-[var(--dojo-maroon-soft)]"
              }`}
              data-testid={`enquiry-item-${e.id}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {!e.read && <span className="w-2 h-2 rounded-full bg-[var(--dojo-maroon)]" />}
                  <span className="font-serif text-lg">{e.name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-[var(--dojo-ink-soft)]">
                    {new Date(e.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 mt-1 text-sm text-[var(--dojo-ink-soft)]">
                  {e.email && (
                    <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-[var(--dojo-maroon)]">
                      <Mail size={13} /> {e.email}
                    </a>
                  )}
                  {e.phone && (
                    <a href={`tel:${e.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-1.5 hover:text-[var(--dojo-maroon)]">
                      <Phone size={13} /> {e.phone}
                    </a>
                  )}
                </div>
                {e.message && <p className="text-sm mt-2">{e.message}</p>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => markRead(e.id, !e.read)}
                  className="btn-outline text-xs flex items-center gap-1.5"
                  data-testid={`enquiry-toggle-read-${e.id}`}
                >
                  <Check size={13} /> {e.read ? "Mark unread" : "Mark read"}
                </button>
                <button
                  onClick={() => remove(e.id)}
                  className="p-2 text-[var(--dojo-hinomaru)] hover:text-[var(--dojo-hinomaru-dark)]"
                  data-testid={`enquiry-delete-${e.id}`}
                  aria-label="Delete enquiry"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
